import axios, { AxiosError, CancelToken } from "axios";
import {
  TOrganization,
  TOrganizationData,
} from "../redux/reducers/organizations";
import {
  TMembershipRole,
  TMembershipStatus,
} from "../redux/reducers/memberships";
import {
  handleGetDataFromLocalStorage,
  handleParseQuerystrings,
  handleSetDataToLocalStorage,
} from "../utils/miscellaneous";
import { localStorageConstants } from "../utils/constants";
import { TAccountData } from "../redux/reducers/account";
import { buildArrayToQueryStringFields } from "../utils/formatters";
import {
  TInvoice,
  TInvoiceData,
  TInvoiceEntryStatus,
  TInvoiceManifestastionStatus,
  TPaymentSlip,
  TPaymentSlipData,
} from "../redux/reducers/globalState";
import { TPayableObjectForm } from "../components/ui-v2/Modal/Forms/AddPayable";
import { TPayableObjectForm as TPayableObjectForm2 } from "../components/ui-v2/Modal/Forms/AddPaymentSlip";
import { TPayableAccount } from "../components/ui-v2/Modal/Forms/LinkPayable";
import {
  mockInvoiceResponse1,
  mockResponse,
  mockResponse2,
  mockResponse3,
} from "../utils/mocks";
import * as Sentry from "@sentry/react";
import { format } from "date-fns/format";
import { ROUTES } from "../router";

export type TApiErrorRaw = {
  response: { data: { detail: string }; status: number };
};

export type TApiError = {
  message: { title: string; description: string };
  status: number;
};

const createApiService = () => {
  const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_BASE_URL,
  });

  const INVALIDATE_STATUS_CONDITION = {
    withCredentials: true,
    validateStatus: (status: number) => status < 400,
  };

  axiosInstance.interceptors.request.use(
    (request) => {
      Sentry.setContext("request", {
        url: request.url,
        method: request.method,
      });

      const nonMandatoryAuthenticationRoutes = ["/login/"];
      if (!nonMandatoryAuthenticationRoutes.includes(request.url ?? "")) {
        const authorization = localStorage.getItem("Authorization");
        if (authorization) {
          request.headers["Authorization"] = authorization;
        }
        return request;
      }
      return request;
    },
    (error) => {
      if (!axios.isCancel(error)) {
        Sentry.captureException(error);

        console.error("error from request: ", error);
        return error;
      }
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      Sentry.setContext("response", {
        url: response.config.url,
        method: response.config.method,
      });

      return response;
    },
    (e: AxiosError) => {
      if (!axios.isCancel(e)) {
        const error = e as AxiosError;

        if (error.status && ![400].includes(error.status)) {
          Sentry.captureException(error);

          if (error.status === 401) {
            localStorage.clear();
            window.location.href = ROUTES.LOGIN;
          }
        }

        return Promise.reject({
          message: {
            title: error.response?.statusText,
            description: error.message,
          },
          status: error.response?.status,
          response: { data: error.response?.data },
        });
      }
    }
  );

  const handleAuthenticateStep1 = async (cancelToken: CancelToken) => {
    const url = "/login/";
    return axiosInstance
      .get(url, { ...INVALIDATE_STATUS_CONDITION, cancelToken })
      .then((response) => {
        if (response.data.redirect) {
          window.location.href = response.data.redirect;
        }
        return response;
      })
      .catch((e) => e);
  };

  const handleAuthenticateStep2 = async (
    search: string,
    cancelToken: CancelToken
  ) => {
    const url = "/login/callback/" + search;

    return axiosInstance
      .get(url, { ...INVALIDATE_STATUS_CONDITION, cancelToken })
      .then((response) => {
        if (response.data?.Authorization) {
          const authorization = `${response.data?.Authorization}`;
          localStorage.setItem(
            localStorageConstants.AUTHORIZATION,
            authorization
          );
        }
        return response;
      })
      .catch((e) => {
        throw e;
      });
  };

  const getUserData = async (cancelToken: CancelToken) => {
    const url = "/users/me/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      handleSetDataToLocalStorage(
        localStorageConstants.USER_DATA,
        response.data
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Create multiple mocked accounts in the backend.
   * @param count Number of accounts to create
   * @param cancelToken Axios CancelToken
   * @returns Array of created accounts
   */
  const createMockAccounts = async (
    count: number,
    cancelToken: CancelToken
  ) => {
    const results: any[] = [];
    for (let i = 0; i < count; i++) {
      const runId = Date.now().toString().slice(-4); // 4-digit run identifier
      const uniqueEmail = `mock_account_${runId}_${i + 1}@example.com`;
      const accountData = {
        name: `Mock Account ${i + 1}`,
        email: uniqueEmail,
        is_active: true,
        // Add other required fields here if needed
      };
      try {
        const response = await axiosInstance.post("/accounts/", accountData, {
          cancelToken,
        });
        results.push(response.data);
      } catch (error) {
        // Optionally handle errors per account
        console.error(`Error creating account ${i + 1}:`, error);
      }
    }
    return results;
  };

  /**
   * Create multiple mocked members in the backend.
   * @param count Number of members to create
   * @param memberTemplate Template object for member fields (except email)
   * @param cancelToken Axios CancelToken
   * @returns Array of created members
   */
  const createMockMembers = async (count: number, cancelToken: CancelToken) => {
    const results: any[] = [];

    const localSelectOrganization = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;

    for (let i = 0; i < count; i++) {
      const runId = Date.now().toString().slice(-4); // 4-digit run identifier
      const uniqueEmail = `mock_member_${runId}_${i + 1}@example.com`;
      const memberData = {
        email: uniqueEmail,
        role: (((i % 3) + 1) * 10) as TMembershipRole,
        is_active: i % 2 === 0,
        account: localSelectOrganization.account.uuid,
        organizations: [localSelectOrganization.uuid],
      };
      try {
        const response = await createMember(memberData, cancelToken);
        results.push(response.data);
      } catch (error) {
        // Optionally handle errors per member
        console.error(`Error creating member ${i + 1}:`, error);
      }
    }
    return results;
  };

  const getAccountsWithBackendUrl = async (
    url: string,
    cancelToken: CancelToken
  ) => {
    const newUrl = new URL(url);
    try {
      const response = await axiosInstance.get(
        newUrl.pathname + newUrl.search,
        {
          cancelToken,
        }
      );
      const accounts = response?.data as TAccountData;
      const localSelectAccount = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_ACCOUNT
      );
      if (!localSelectAccount && accounts) {
        if (accounts.results.length === 1) {
          handleSetDataToLocalStorage(
            localStorageConstants.SELECT_ACCOUNT,
            accounts.results[0]
          );
        }
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAccounts = async (
    cancelToken: CancelToken,
    search?: string,
    page?: number,
    per_page?: number
  ) => {
    const url = "/accounts/";
    try {
      const accountsResponse = await axiosInstance.get(url, {
        cancelToken,
        params: {
          page,
          search: decodeURIComponent(search ?? "").replace(/\+/g, " "),
          per_page,
        },
      });
      const localSelectAccount = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_ACCOUNT
      );
      if (!localSelectAccount && accountsResponse) {
        const accounts = accountsResponse.data as TAccountData;
        if (accounts.results.length === 1) {
          handleSetDataToLocalStorage(
            localStorageConstants.SELECT_ACCOUNT,
            accounts.results[0]
          );
        }
      }
      return accountsResponse;
    } catch (error) {
      throw error;
    }
  };

  const getAccount = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/accounts/" + uuid;
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const synchronizeAccount = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/accounts/" + uuid + "/synchronize/";
    try {
      const response = await axiosInstance.post(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrganization = async (
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/organizations/?uuid=" + organizationUuid;
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizations = async (
    accountUuid: string,
    cancelToken: CancelToken,
    page?: number,
    search?: string,
    per_page?: number
  ) => {
    const url = "/organizations/?account=" + accountUuid;
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: {
          page: page,
          per_page: per_page,
          search: decodeURIComponent(search ?? "").replace(/\+/g, " "),
        },
      });
      handleSetDataToLocalStorage(
        localStorageConstants.ORGANIZATIONS_COUNT,
        (response?.data as TOrganizationData)?.count
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationsWithBackendUrl = async (
    url: string,
    cancelToken: CancelToken
  ) => {
    const newUrl = new URL(url);
    try {
      const response = await axiosInstance.get(
        newUrl.pathname + newUrl.search,
        {
          cancelToken,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getOrganizationPermissions = async (
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "organizations/" + organizationUuid + "/permissions/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAllOrganizations = async (
    selectAccountId: string,
    cancelToken: CancelToken,
    searchPhrase?: string
  ) => {
    try {
      const organizationsResponse = (
        await getOrganizations(
          selectAccountId,
          cancelToken,
          undefined,
          searchPhrase
        )
      )?.data ?? { results: [], current_page: 1, count: 0 };

      let currentPage = organizationsResponse.current_page;

      const orgArray: TOrganization[] = organizationsResponse.results;
      const lastPage = Math.ceil(
        organizationsResponse.count / organizationsResponse.results.length
      );

      while (currentPage++ < lastPage) {
        await apiService
          .getOrganizations(
            selectAccountId,
            cancelToken,
            currentPage,
            searchPhrase
          )
          .then((response: { data: TOrganizationData }) => {
            if (
              response &&
              response.data &&
              Array.isArray(response.data.results)
            ) {
              response.data.results.forEach((value) => {
                orgArray.push(value);
              });
            }
            return response;
          });
      }

      return orgArray;
    } catch (error) {
      throw error;
    }
  };

  const toggleOrganizationVisibility = async (
    organizationId: string,
    isActive: boolean,
    cancelToken: CancelToken
  ) => {
    const url = `/organizations/${organizationId}/visibility/`;
    try {
      const response = await axiosInstance.put(url, {
        cancelToken,
        is_active: !isActive,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const filterOrganizationByVisibility = async (
    uuid: string,
    isActive: undefined | boolean,
    cancelToken: CancelToken,
    search?: string,
    page?: number
  ) => {
    const url = "/organizations/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: {
          account: uuid,
          is_active: isActive,
          search: decodeURIComponent(search ?? "").replace(/\+/g, " "),
          page: page,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getMemberships = async (
    uuid: string,
    cancelToken: CancelToken,
    page?: number,
    role?: (TMembershipRole | undefined)[],
    status?: (TMembershipStatus | undefined)[]
  ) => {
    const queryStrings = handleParseQuerystrings(window.location.search);

    let url = "/memberships/?account=" + uuid;
    if (role && role.length > 0 && !role.includes(undefined))
      url += buildArrayToQueryStringFields(role, "role");
    if (status && status.length > 0 && !status.includes(undefined))
      url += buildArrayToQueryStringFields(status, "status");

    if (window.location.pathname.includes("settings/users/edit/")) {
      delete queryStrings.search;
    }

    const search = decodeURIComponent(
      queryStrings.members_search?.[0] ?? ""
    ).replace(/\+/g, " ");

    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: {
          page: page,
          search: search ? search : undefined,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createMember = async (
    member: {
      account: string;
      email: string;
      role: TMembershipRole;
      is_active: boolean;
      organizations: string[];
    },
    cancelToken: CancelToken
  ) => {
    const url = "/memberships/";
    try {
      const response = await axiosInstance.post(url, member, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const editMember = async (
    uuid: string,
    member: {
      account: string;
      email: string;
      role: TMembershipRole;
      is_active: boolean;
      organizations: string[];
    },
    cancelToken: CancelToken
  ) => {
    const url = "/memberships/" + uuid + "/";
    try {
      const response = await axiosInstance.put(
        url,
        {
          role: member.role,
          is_active: member.is_active,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteMember = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/memberships/" + uuid + "/";
    try {
      const response = await axiosInstance.delete(url, {
        params: { account: uuid },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getInvoices = async (
    params: {
      organizationUuid: string;
      manifestation_status?: number;
      launch_status?: number;
      date_issue_before?: string;
      date_issue_after?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken,
    mock?: { type: "resolve"; data: TInvoiceData } | { type: "reject" }
  ) => {
    if (mock) {
      const response = new Promise((resolve, reject) => {
        setTimeout(() => {
          if (mock.type === "resolve") {
            resolve({ data: mock.data });
          } else {
            reject({
              status: 500,
              response: { data: { detail: "error test" } },
            });
          }
        }, 2000);
      });
      return response as PromiseLike<{
        data: TInvoiceData;
      }>;
    } else {
      const url = "/invoices/";
      try {
        const response = await axiosInstance.get(url, {
          cancelToken,
          params: {
            organization: params.organizationUuid,
            manifestation: params.manifestation_status,
            launch_status: params.launch_status,
            date_issue_before: "undefined".includes(
              params.date_issue_after ?? ""
            )
              ? format(new Date(), "yyyy-MM-dd")
              : params.date_issue_after,
            date_issue_after: params.date_issue_before,
            has_goods_receipt: params.has_goods_receipt,
            has_accounts_payable: params.has_accounts_payable,
            page: params.page,
            search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
            per_page: params.per_page,
          },
        });
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  const getInvoicesCompressedFilesDownloadLink = async (
    params: {
      option: "xml" | "darf";
      itemsUuid: string[];
      organizationUuid: string;

      manifestation_status?: number;
      launch_status?: number;
      date_issue_before?: string;
      date_issue_after?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const route = "/invoices/download/" + params.option + "/";
    try {
      const response = await axiosInstance.get(route, {
        cancelToken,
        params: {
          organization: params.organizationUuid,
          manifestation: params.manifestation_status,
          launch_status: params.launch_status,
          date_issue_before: params.date_issue_after,
          date_issue_after: params.date_issue_before,
          has_goods_receipt: params.has_goods_receipt,
          has_accounts_payable: params.has_accounts_payable,
          page: params.page,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
          per_page: params.per_page,
          uuid: params.itemsUuid.toString(),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAllInvoices = async (
    params: {
      organizationUuid: string;
      manifestation_status?: number;
      launch_status?: number;
      date_issue_before?: string;
      date_issue_after?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      per_page?: number;
      search?: string;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/";
    try {
      const invoicesResponse = (
        await axiosInstance.get(url, {
          cancelToken,
          params: {
            organization: params.organizationUuid,
            manifestation: params.manifestation_status,
            launch_status: params.launch_status,
            date_issue_before: params.date_issue_after,
            date_issue_after: params.date_issue_before,
            has_goods_receipt: params.has_goods_receipt,
            has_accounts_payable: params.has_accounts_payable,
            page: params.page,
            per_page: params.per_page,
            search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
          },
        })
      ).data as TInvoiceData;

      let currentPage = invoicesResponse.current_page;

      const invoicesArray: TInvoice[] = invoicesResponse.results;
      const lastPage = Math.ceil(
        invoicesResponse.count / invoicesResponse.results.length
      );

      while (currentPage++ < lastPage) {
        await apiService
          .getInvoices({ ...params, page: currentPage }, cancelToken)
          .then((response: { data: TInvoiceData }) => {
            response.data.results.forEach((value) => {
              invoicesArray.push(value);
            });
            return response;
          });
      }

      return invoicesArray;
    } catch (error) {
      throw error;
    }
  };

  const getInvoice = async (
    invoiceId: string,
    cancelToken: CancelToken,
    mock?: { type: "resolve"; nexaasState: number } | { type: "reject" }
  ) => {
    if (mock) {
      const response = new Promise((resolve, reject) => {
        setTimeout(() => {
          if (mock.type === "resolve") {
            resolve({
              data: {
                ...mockInvoiceResponse1.results[0],
                goods_receipt_url: "https://google.com.br",
                nexaas_state: mock.nexaasState,
                nexaas_finished_at:
                  mock.nexaasState == 60 ? "2019-08-24T14:15:22Z" : null,
              },
            });
          } else {
            reject({
              status: 500,
              response: { data: { detail: "error test" } },
            });
          }
        }, 2000);
      });
      return response;
    } else {
      const url = "/invoices/" + invoiceId + "/";
      try {
        const response = await axiosInstance.get(url, { cancelToken });
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  const getInvoiceUpdatedAt = async (
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/updated-at/";
    try {
      const response = await axiosInstance.get(url, {
        params: { organization: organizationUuid },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateInvoiceManifestation = async (
    uuid: string,
    manifestation: TInvoiceManifestastionStatus,
    cancelToken: CancelToken,
    justification?: string
  ) => {
    const url = "/invoices/" + uuid + "/manifestation/";
    try {
      const response = await axiosInstance.put(
        url,
        {
          manifestation,
          justification,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateInvoiceMarkAsFinished = async (
    uuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + uuid + "/finalize-launch/";
    try {
      const response = await axiosInstance.put(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateInvoiceMarkAsPending = async (
    uuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + uuid + "/hang-launch/";
    try {
      const response = await axiosInstance.put(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlips = async (
    params: {
      organizationUuid: string;
      due_date_after?: string;
      due_date_before?: string;
      launch_status?: number;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/";
    try {
      const temp = { ...params } as any;
      temp.organizationUuid = undefined;
      temp.organization = params.organizationUuid;
      const response = await axiosInstance.get(url, {
        params: {
          ...temp,
          due_date_before: params.due_date_before,
          due_date_after: params.due_date_after,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
        },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getInvoiceCounters = async (
    params: {
      organizationUuid: string;

      manifestation_status?: number;
      launch_status?: number;
      date_issue_before?: string;
      date_issue_after?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      per_page?: number;
      search?: string;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/count/";
    try {
      const temp = { ...params } as any;
      temp.organizationUuid = undefined;
      const response = await axiosInstance.get(url, {
        params: {
          ...temp,
          organization: params.organizationUuid,
          date_issue_before: "undefined".includes(params.date_issue_after ?? "")
            ? format(new Date(), "yyyy-MM-dd")
            : params.date_issue_after,
          date_issue_after: params.date_issue_before,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
        },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlipCounters = async (
    params: {
      organizationUuid: string;

      due_date_after?: string;
      due_date_before?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/count/";
    try {
      const temp = { ...params } as any;
      delete temp.page;
      delete temp.per_page;
      temp.organizationUuid = undefined;
      temp.organization = params.organizationUuid;
      const response = await axiosInstance.get(url, {
        params: {
          ...temp,
          due_date_before: "undefined".includes(params.due_date_before ?? "")
            ? format(new Date(), "yyyy-MM-dd")
            : params.due_date_before,
          due_date_after: params.due_date_after,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
        },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlipsCompressedFilesDownloadLink = async (
    params: {
      itemsUuid: string[];
      organizationUuid: string;

      manifestation_status?: number;
      launch_status?: number;
      date_issue_before?: string;
      date_issue_after?: string;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const route = "/boletos/download/";
    try {
      const response = await axiosInstance.get(route, {
        cancelToken,
        params: {
          organization: params.organizationUuid,
          manifestation: params.manifestation_status,
          launch_status: params.launch_status,
          date_issue_before: params.date_issue_after,
          date_issue_after: params.date_issue_before,
          has_goods_receipt: params.has_goods_receipt,
          has_accounts_payable: params.has_accounts_payable,
          page: params.page,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
          per_page: params.per_page,
          uuid: params.itemsUuid.toString(),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAllPaymentSlips = async (
    params: {
      organizationUuid: string;
      due_date_after?: string;
      due_date_before?: string;
      launch_status?: TInvoiceEntryStatus;
      has_accounts_payable?: boolean;
      has_goods_receipt?: boolean;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/";
    try {
      const temp = { ...params } as any;
      if ("undefined".includes(temp.due_date_after)) {
        temp.due_date_after = undefined;
      }
      if ("undefined".includes(temp.due_date_before)) {
        temp.due_date_before = undefined;
      }
      temp.organizationUuid = undefined;
      temp.organization = params.organizationUuid;
      const paymentSlipsResponse = (
        await axiosInstance.get(url, {
          params: {
            ...temp,
            search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
          },
          cancelToken,
        })
      )?.data as TPaymentSlipData;
      if (paymentSlipsResponse) {
        let currentPage = paymentSlipsResponse.current_page;
        const paymentSlipsArray: TPaymentSlip[] = paymentSlipsResponse.results;
        const lastPage = Math.ceil(
          paymentSlipsResponse.count / paymentSlipsResponse.results.length
        );
        while (currentPage++ < lastPage) {
          await apiService
            .getPaymentSlips({ ...params, page: currentPage }, cancelToken)
            .then((response: { data: TPaymentSlipData }) => {
              response.data.results.forEach((value) => {
                paymentSlipsArray.push(value);
              });
              return response;
            });
        }
        return paymentSlipsArray;
      }
      return [];
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlip = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/boletos/" + uuid + "/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlipAttachedInvoices = async (
    uuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/" + uuid + "/invoices/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getInvoiceAttachedPaymentSlips = async (
    uuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + uuid + "/boletos/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPaymentSlipUpdatedAt = async (
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/updated-at/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: {
          organization: organizationUuid,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deletePaymentSlip = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/boletos/" + uuid + "/";
    try {
      const response = await axiosInstance.delete(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updatePaymentSlipLaunchStatus = async (
    uuid: string,
    action: "decline" | "hang" | "finalize",
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/" + uuid + "/" + action + "-launch/";
    try {
      const response = await axiosInstance.put(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const invoiceImportFiles = async (
    organizationUuid: string,
    formData: FormData,
    fileType: "danfe" | "xml",
    cancelToken: CancelToken
  ) => {
    const url =
      `/invoices/import-${fileType}/?organization=` + organizationUuid;
    try {
      const response = await axiosInstance.post(url, formData, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const paymentSlipImportPDF = async (
    organizationUuid: string,
    formData: FormData,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/import/?organization=" + organizationUuid;
    try {
      const response = await axiosInstance.post(url, formData, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const detachInvoiceFromPaymentSlip = async (
    paymentSlipUuid: string,
    invoiceUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/" + paymentSlipUuid + "/detach-invoice/";
    try {
      const response = await axiosInstance.put(
        url,
        { invoice: invoiceUuid },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const attachInvoiceToPaymentSlip = async (
    paymentSlipUuid: string,
    invoiceUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/" + paymentSlipUuid + "/attach-invoice/";
    try {
      const response = await axiosInstance.put(
        url,
        { invoice: invoiceUuid },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const detachPaymentSlipFromInvoice = async (
    invoiceUuid: string,
    paymentSlipUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + invoiceUuid + "/detach-boleto/";
    try {
      const response = await axiosInstance.put(
        url,
        { boleto: paymentSlipUuid },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const attachPaymentSlipToInvoice = async (
    invoiceUuid: string,
    paymentSlipUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + invoiceUuid + "/attach-boleto/";
    try {
      const response = await axiosInstance.put(
        url,
        { boleto: paymentSlipUuid },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCategories = async (cancelToken: CancelToken) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/categories/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getClassificationCenters = async (cancelToken: CancelToken) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/classification-centers/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getSuppliers = async (cancelToken: CancelToken) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/suppliers/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getInvoiceSupplier = async (
    organizationId: string,
    invoiceId: string,
    cancelToken: CancelToken
  ) => {
    const url =
      "/fintera/" + organizationId + "/invoice/" + invoiceId + "/supplier/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getDepositAccounts = async (cancelToken: CancelToken) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/deposit-accounts/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createInCashPayable = async (
    form: TPayableObjectForm | TPayableObjectForm2,
    cancelToken: CancelToken
  ) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/payable-accounts/in-cash/";
    try {
      const response = await axiosInstance.post(url, form, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createRecurringPayable = async (
    form: TPayableObjectForm | TPayableObjectForm2,
    cancelToken: CancelToken
  ) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/payable-accounts/recurrent/";
    try {
      const response = await axiosInstance.post(url, form, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createInstallmentsPayable = async (
    form: TPayableObjectForm | TPayableObjectForm2,
    cancelToken: CancelToken
  ) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url =
      "/fintera/" + organizationId + "/payable-accounts/installments/";
    try {
      const response = await axiosInstance.post(url, form, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getPayableAccounts = async (
    cancelToken: CancelToken,
    params?: {
      amount?: string;
      category?: string;
      description?: string;
      due_date?: string;
      paid_amount?: string;
      payment_date?: string;
      supplier_name?: string;
      page?: number;
      per_page?: number;
    }
  ) => {
    const { uuid: organizationId } = handleGetDataFromLocalStorage(
      localStorageConstants.SELECT_ORGANIZATION
    ) as TOrganization;
    const url = "/fintera/" + organizationId + "/payable-accounts/";
    try {
      const response = await axiosInstance.get(url, {
        params: { ...params },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getAllPayableAccounts = async (
    cancelToken: CancelToken,
    params?: {
      amount?: string;
      category?: string;
      description?: string;
      due_date?: string;
      paid_amount?: string;
      payment_date?: string;
      supplier_name?: string;
      page?: number;
    }
  ) => {
    try {
      const payableAccountsResponse = (
        await getPayableAccounts(cancelToken, params)
      ).data;
      let currentPage = payableAccountsResponse.current_page;
      const payableArray: TPayableAccount[] = payableAccountsResponse.results;
      const lastPage = Math.ceil(
        payableAccountsResponse.pages.length /
          payableAccountsResponse.results.length
      );
      while (currentPage++ < lastPage) {
        await getPayableAccounts(cancelToken, { page: currentPage++ }).then(
          (response: { data: { results: TPayableAccount[] } }) => {
            response.data.results.forEach((value) => {
              payableArray.push(value);
            });
            return response;
          }
        );
      }
      return payableArray;
    } catch (error) {
      throw error;
    }
  };

  const setLinkInvoiceToPayable = async (
    invoiceId: string,
    documentToLinkId: string,
    cancelToken: CancelToken
  ) => {
    const url = "/invoices/" + invoiceId + "/attach-bill/";
    try {
      const response = await axiosInstance.put(
        url,
        {
          bill_id: documentToLinkId,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const setLinkPaymentSlipToPayable = async (
    paymentSlipId: string,
    documentToLinkId: string,
    cancelToken: CancelToken
  ) => {
    const url = "/boletos/" + paymentSlipId + "/attach-bill/";
    try {
      const response = await axiosInstance.put(
        url,
        {
          bill_id: documentToLinkId,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getGoodsStock = async (
    organizationId: string,
    cancelToken: CancelToken
  ) => {
    const url = "/nexaas/" + organizationId + "/stocks/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getCachedGoodsExpressModeStatus = async (
    organizationId: string,
    invoiceId: string,
    cancelToken: CancelToken
  ) => {
    const url =
      "/nexaas/" +
      organizationId +
      "/stocks/invoices/" +
      invoiceId +
      "/verify/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getNonCachedGoodsExpressModeStatus = async (
    organizationId: string,
    invoiceId: string,
    cancelToken: CancelToken,
    mock?:
      | { type: "resolve"; model: "mock1" | "mock2" | "mock3" }
      | { type: "reject" }
  ) => {
    if (mock) {
      const response = new Promise((resolve, reject) => {
        setTimeout(() => {
          if (cancelToken.reason) {
            reject(new axios.Cancel("Request canceled by user"));
          }
          if (mock.type === "resolve") {
            switch (mock.model) {
              case "mock1":
                resolve({ data: mockResponse });
                break;
              case "mock2":
                resolve({ data: mockResponse2 });
                break;
              case "mock3":
                resolve({ data: mockResponse3 });
                break;
            }
          } else {
            reject({
              response: { data: { detail: "error test" } },
              status: 500,
            });
          }
        }, 5000);
      });
      return response;
    } else {
      const url =
        "/nexaas/" +
        organizationId +
        "/stocks/invoices/" +
        invoiceId +
        "/verify/";
      try {
        const response = await axiosInstance.post(url, { cancelToken });
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  const setGoods = async (
    organizationId: string,
    stockId: number,
    invoiceId: string,
    expressMode: boolean,
    cancelToken: CancelToken,
    mock?: "resolve" | "reject"
  ) => {
    if (mock) {
      const response = new Promise((resolve, reject) => {
        setTimeout(() => {
          if (mock === "resolve") {
            resolve({ data: { status: 201 } });
          } else {
            reject({
              status: 500,
              response: { data: { detail: "error test" } },
            });
          }
        }, 2000);
      });
      return response;
    } else {
      const url = "/nexaas/" + organizationId + "/stocks/invoices/";
      try {
        const response = await axiosInstance.post(
          url,
          {
            stock_id: stockId,
            invoice_id: invoiceId,
            express_mode: expressMode,
          },
          { cancelToken }
        );
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  const updateDefaultGoodsStockId = async (
    organizationId: string,
    stockId: number,
    cancelToken: CancelToken
  ) => {
    const url = "/organizations/" + organizationId + "/nexaas/stock/";
    try {
      const response = await axiosInstance.put(
        url,
        {
          default_nexaas_stock_id: stockId,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createExpressGoodsStockEntry = async (
    organizationId: string,
    invoiceId: string,
    cancelToken: CancelToken,
    mock?: { type: "resolve" } | { type: "reject" }
  ) => {
    if (mock) {
      const response = new Promise((resolve, reject) => {
        setTimeout(() => {
          if (mock.type === "resolve") {
            resolve({});
          } else {
            reject({
              status: 500,
              response: { data: { detail: "error test" } },
            });
          }
        }, 3000);
      });
      return response;
    } else {
      const url =
        "/nexaas/" +
        organizationId +
        "/stocks/invoices/" +
        invoiceId +
        "/launch/";
      try {
        const response = await axiosInstance.post(url, { cancelToken });
        return response;
      } catch (error) {
        throw error;
      }
    }
  };

  const getOrganizationsByMembership = async (
    membershipUuid: string,
    cancelToken: CancelToken
  ) => {
    const route = "/memberships/" + membershipUuid + "/list-organizations/";
    try {
      const response = await axiosInstance.get(route, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const setOrganizationsByMembership = async (
    membershipUuid: string,
    organizationsUuid: string[],
    cancelToken: CancelToken
  ) => {
    const route = "/memberships/" + membershipUuid + "/organizations/";
    try {
      const response = await axiosInstance.put(
        route,
        {
          organizations: organizationsUuid,
        },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const renamePaymentSlipLabel = async (
    paymentSlipUuid: string,
    label: string,
    cancelToken: CancelToken
  ) => {
    const route = "/boletos/" + paymentSlipUuid + "/edit-label/";
    try {
      const response = await axiosInstance.put(
        route,
        { label },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const renameInvoiceLabel = async (
    invoiceUuid: string,
    label: string,
    cancelToken: CancelToken
  ) => {
    const route = "/invoices/" + invoiceUuid + "/edit-label/";
    try {
      const response = await axiosInstance.put(
        route,
        { label },
        { cancelToken }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getQueueNFE = async (
    params: { cnpj: string; hasError?: boolean },
    cancelToken: CancelToken
  ) => {
    const route = "/certificates/queue-nfe/";
    try {
      const response = await axiosInstance.get(route, {
        params: {
          cnpj: params.cnpj,
          has_error: params.hasError,
        },
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const postNFEQueue = async (
    params: { cnpj: string; access_key: string },
    cancelToken: CancelToken
  ) => {
    const route = "/certificates/queue-nfe/";
    try {
      const response = await axiosInstance.post(route, params, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const deleteNFEQueue = async (
    params: { uuid: string },
    cancelToken: CancelToken
  ) => {
    const route = "/certificates/queue-nfe/" + params.uuid + "/";
    try {
      const response = await axiosInstance.delete(route, {
        cancelToken,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  // ===========================================================================
  // Notas emitidas (issued_invoices) — API real que substitui o mock.
  // Backend: backend/application/issued_invoices/.
  // ===========================================================================

  const getIssuedInvoices = async (
    params: {
      organizationUuid: string;
      emission_status?: number[];
      nature?: number[];
      model?: number;
      issue_date_before?: string;
      issue_date_after?: string;
      page?: number;
      search?: string;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/issued-invoices/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        // Arrays sem colchetes: emission_status=20&emission_status=40 (o
        // DjangoFilter MultipleChoiceFilter ignora o formato com "[]").
        paramsSerializer: { indexes: null },
        params: {
          organization: params.organizationUuid,
          emission_status: params.emission_status,
          nature: params.nature,
          model: params.model,
          issue_date_before: params.issue_date_after,
          issue_date_after: params.issue_date_before,
          page: params.page,
          per_page: params.per_page,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoice = async (uuid: string, cancelToken: CancelToken) => {
    const url = "/issued-invoices/" + uuid + "/";
    try {
      const response = await axiosInstance.get(url, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoicesCount = async (
    params: {
      organizationUuid: string;
      emission_status?: number[];
      nature?: number[];
      issue_date_before?: string;
      issue_date_after?: string;
      search?: string;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/issued-invoices/count/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        paramsSerializer: { indexes: null },
        params: {
          organization: params.organizationUuid,
          emission_status: params.emission_status,
          nature: params.nature,
          issue_date_before: params.issue_date_after,
          issue_date_after: params.issue_date_before,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoicesUpdatedAt = async (
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/issued-invoices/updated-at/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: { organization: organizationUuid },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoicesCompressedFilesDownloadLink = async (
    params: {
      option: "xml" | "pdf";
      organizationUuid: string;
      itemsUuid?: string[];
      emission_status?: number[];
      nature?: number[];
      issue_date_before?: string;
      issue_date_after?: string;
      search?: string;
    },
    cancelToken: CancelToken
  ) => {
    const route = "/issued-invoices/download/" + params.option + "/";
    try {
      const response = await axiosInstance.get(route, {
        cancelToken,
        paramsSerializer: { indexes: null },
        params: {
          organization: params.organizationUuid,
          emission_status: params.emission_status,
          nature: params.nature,
          issue_date_before: params.issue_date_after,
          issue_date_after: params.issue_date_before,
          search: decodeURIComponent(params.search ?? "").replace(/\+/g, " "),
          uuid: params.itemsUuid?.toString(),
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoiceExports = async (
    params: {
      organizationUuid: string;
      status?: number[];
      page?: number;
      per_page?: number;
    },
    cancelToken: CancelToken
  ) => {
    const url = "/issued-invoice-exports/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        paramsSerializer: { indexes: null },
        params: {
          organization: params.organizationUuid,
          status: params.status,
          page: params.page,
          per_page: params.per_page,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const getIssuedInvoiceExport = async (
    uuid: string,
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url = "/issued-invoice-exports/" + uuid + "/";
    try {
      const response = await axiosInstance.get(url, {
        cancelToken,
        params: { organization: organizationUuid },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const createIssuedInvoiceExport = async (
    organizationUuid: string,
    form: {
      start_date?: string;
      end_date?: string;
      status_operator?: number;
      filter_status?: number[];
      nature_operator?: number;
      filter_nature?: number[];
      export_files?: string[];
    },
    cancelToken: CancelToken
  ) => {
    const url =
      "/issued-invoice-exports/?organization=" + organizationUuid;
    try {
      const response = await axiosInstance.post(url, form, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const retryIssuedInvoiceExport = async (
    uuid: string,
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url =
      "/issued-invoice-exports/" +
      uuid +
      "/retry/?organization=" +
      organizationUuid;
    try {
      const response = await axiosInstance.post(url, {}, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  const duplicateIssuedInvoiceExport = async (
    uuid: string,
    organizationUuid: string,
    cancelToken: CancelToken
  ) => {
    const url =
      "/issued-invoice-exports/" +
      uuid +
      "/duplicate/?organization=" +
      organizationUuid;
    try {
      const response = await axiosInstance.post(url, {}, { cancelToken });
      return response;
    } catch (error) {
      throw error;
    }
  };

  return {
    handleAuthenticateStep1,
    handleAuthenticateStep2,
    getUserData,
    getAccountsWithBackendUrl,
    getAccounts,
    getAccount,
    synchronizeAccount,
    getMemberships,
    createMember,
    editMember,
    deleteMember,
    getOrganizations,
    getOrganization,
    getOrganizationsWithBackendUrl,
    getOrganizationPermissions,
    getAllOrganizations,
    toggleOrganizationVisibility,
    filterOrganizationByVisibility,
    getInvoices,
    getInvoicesCompressedFilesDownloadLink,
    getPaymentSlipsCompressedFilesDownloadLink,
    getInvoiceCounters,
    getPaymentSlipCounters,
    getAllInvoices,
    getInvoice,
    updateInvoiceManifestation,
    updateInvoiceMarkAsFinished,
    updateInvoiceMarkAsPending,
    getPaymentSlips,
    getPaymentSlip,
    getPaymentSlipAttachedInvoices,
    getInvoiceAttachedPaymentSlips,
    getAllPaymentSlips,
    deletePaymentSlip,
    updatePaymentSlipLaunchStatus,
    invoiceImportFiles,
    paymentSlipImportPDF,
    detachInvoiceFromPaymentSlip,
    attachInvoiceToPaymentSlip,
    detachPaymentSlipFromInvoice,
    attachPaymentSlipToInvoice,
    getCategories,
    getClassificationCenters,
    getSuppliers,
    getInvoiceSupplier,
    getDepositAccounts,
    createInCashPayable,
    createInstallmentsPayable,
    createRecurringPayable,
    getPayableAccounts,
    getAllPayableAccounts,
    setLinkInvoiceToPayable,
    setLinkPaymentSlipToPayable,
    getGoodsStock,
    getCachedGoodsExpressModeStatus,
    getNonCachedGoodsExpressModeStatus,
    createExpressGoodsStockEntry,
    setGoods,
    updateDefaultGoodsStockId,
    getInvoiceUpdatedAt,
    getPaymentSlipUpdatedAt,
    getOrganizationsByMembership,
    setOrganizationsByMembership,
    renamePaymentSlipLabel,
    renameInvoiceLabel,
    createMockAccounts,
    createMockMembers,
    getQueueNFE,
    postNFEQueue,
    deleteNFEQueue,
    getIssuedInvoices,
    getIssuedInvoice,
    getIssuedInvoicesCount,
    getIssuedInvoicesUpdatedAt,
    getIssuedInvoicesCompressedFilesDownloadLink,
    getIssuedInvoiceExports,
    getIssuedInvoiceExport,
    createIssuedInvoiceExport,
    retryIssuedInvoiceExport,
    duplicateIssuedInvoiceExport,
  };
};

const apiService = createApiService();

export type TApiService = typeof apiService;

export default apiService;
